import { messageForError } from '../../api/errors'
import type { Household } from '../../api/households'
import type { User } from '../../api/users'
import { Button } from '../ui/Button'
import { Card, Rows } from './Card'
import { ChildCard } from './ChildCard'

interface FamilySectionProps {
  /** Undefined while loading; null for a parent who owns no household. */
  household: Household | null | undefined
  /** Undefined until the household, and then the children, have loaded. */
  kids: User[] | undefined
  /** Whatever stopped either of those from loading. */
  error: unknown
  retry(): void
  studioUrl: string
  checkoutUrl: string
}

/** The kids come first: this is what a parent opens the page for. One card, each child a card within it. */
export function FamilySection({
  household,
  kids,
  error,
  retry,
  studioUrl,
  checkoutUrl,
}: FamilySectionProps) {
  return (
    <Card
      header={<h2 className="text-2xl font-bold tracking-tight text-ink">Your Family</h2>}
      testId="family-section"
    >
      <FamilyContent
        household={household}
        kids={kids}
        error={error}
        retry={retry}
        studioUrl={studioUrl}
        checkoutUrl={checkoutUrl}
      />
    </Card>
  )
}

function FamilyContent({
  household,
  kids,
  error,
  retry,
  studioUrl,
  checkoutUrl,
}: FamilySectionProps) {
  if (error) {
    return (
      <Notice testId="family-error">
        <p role="alert">{messageForError(error)}</p>
        <Button variant="secondary" onClick={retry}>
          Try again
        </Button>
      </Notice>
    )
  }

  if (household === null) {
    return (
      <Notice testId="family-empty">
        <p>Your account isn’t part of a household yet.</p>
      </Notice>
    )
  }

  if (!household || !kids) return <Skeleton />

  if (kids.length === 0) {
    return (
      <Notice testId="family-empty">
        <p>No kids yet. They’re added to your family when you complete checkout.</p>
        <a
          href={checkoutUrl}
          className="text-brand underline underline-offset-2 hover:text-brand-hover"
        >
          Go to checkout
        </a>
      </Notice>
    )
  }

  return (
    <div className="flex flex-col gap-4 px-6 py-5">
      <p className="text-sm text-ink-soft" data-testid="family-intro">
        Manage your kids’ profiles and how they play, create, and share on Codeverse.
      </p>
      {kids.map((child) => (
        <ChildCard
          key={child.id}
          child={child}
          kids={kids}
          household={household}
          studioUrl={studioUrl}
        />
      ))}
    </div>
  )
}

/** What stands in for the kids: an error, or a family with none yet. */
function Notice({ testId, children }: { testId: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-3 px-6 py-5 text-ink-soft" data-testid={testId}>
      {children}
    </div>
  )
}

/** The shape of a child card, so the section does not jump when the real ones arrive. */
function Skeleton() {
  return (
    <output aria-label="Loading your family" className="block px-6 py-5 motion-safe:animate-pulse">
      <Card
        nested
        header={
          <div className="flex items-center gap-4">
            <div className="size-14 rounded-full bg-field" />
            <div className="flex-1">
              <div className="h-5 w-32 rounded bg-field" />
              <div className="mt-2 h-4 w-48 rounded bg-field" />
            </div>
            <div className="h-10 w-28 rounded-full bg-field" />
          </div>
        }
      >
        <Rows>
          <div className="h-10 py-3" />
          <div className="h-10 py-3" />
          <div className="h-10 py-3" />
        </Rows>
      </Card>
    </output>
  )
}
